export type AnnouncePoliteness = 'polite' | 'assertive';

/**
 * Forked from @react-aria/live-announcer (Apache-2.0).
 * Source: https://github.com/adobe/react-spectrum/tree/main/packages/@react-aria/live-announcer
 *
 * Key adaptations:
 * - TypeScript rewrite with explicit types
 * - Vitest detection alongside Jest/React-act-environment
 * - Plain aria-live divs instead of role="log" (more explicit, same AT behaviour)
 */

/** Returns true inside Jest/Vitest/React Testing Library environments. */
function isTestEnvironment(): boolean {
  const g = globalThis as Record<string, unknown>;
  if (typeof g['IS_REACT_ACT_ENVIRONMENT'] === 'boolean') {
    return g['IS_REACT_ACT_ENVIRONMENT'];
  }
  return typeof g['jest'] !== 'undefined' || typeof g['vi'] !== 'undefined';
}

class LiveAnnouncer {
  private container: HTMLElement | null = null;
  private assertiveLog: HTMLElement | null = null;
  private politeLog: HTMLElement | null = null;

  constructor() {
    if (typeof document === 'undefined') return;

    this.container = document.createElement('div');
    this.container.dataset.liveAnnouncer = 'true';
    // Visually hidden, but accessible to AT
    Object.assign(this.container.style, {
      border: '0',
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      height: '1px',
      margin: '-1px',
      overflow: 'hidden',
      padding: '0',
      position: 'absolute',
      width: '1px',
      whiteSpace: 'nowrap',
    });

    this.assertiveLog = this.createLog('assertive');
    this.container.appendChild(this.assertiveLog);

    this.politeLog = this.createLog('polite');
    this.container.appendChild(this.politeLog);

    document.body.prepend(this.container);
  }

  /**
   * Creates a live region log element.
   * Uses explicit aria-live + aria-relevant="additions" rather than implicit role attributes
   * so individual appended nodes are announced independently (no aria-atomic).
   */
  private createLog(live: AnnouncePoliteness): HTMLElement {
    const el = document.createElement('div');
    el.setAttribute('aria-live', live);
    el.setAttribute('aria-relevant', 'additions');
    return el;
  }

  isAttached(): boolean {
    return this.container?.isConnected ?? false;
  }

  announce(message: string, politeness: AnnouncePoliteness = 'assertive', timeout = 7000): void {
    if (!this.container) return;

    const node = document.createElement('div');
    node.textContent = message;

    if (politeness === 'assertive') {
      this.assertiveLog?.appendChild(node);
    } else {
      this.politeLog?.appendChild(node);
    }

    // Housekeeping only — removals do not trigger AT announcements
    if (message !== '') {
      setTimeout(() => node.remove(), timeout);
    }
  }

  clear(politeness?: AnnouncePoliteness): void {
    if (!this.container) return;
    if (!politeness || politeness === 'assertive') {
      if (this.assertiveLog) this.assertiveLog.innerHTML = '';
    }
    if (!politeness || politeness === 'polite') {
      if (this.politeLog) this.politeLog.innerHTML = '';
    }
  }

  destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}

let announcer: LiveAnnouncer | null = null;

/**
 * Announces a message to screen readers via a singleton pair of off-screen live regions.
 *
 * Uses `aria-live="assertive"` for errors and `aria-live="polite"` for everything else.
 * Each call appends a new node to the relevant region — identical messages re-announce
 * correctly because each call is a distinct DOM mutation.
 *
 * On the very first call the live regions are created and a 100ms delay is applied before
 * the first announcement so assistive technology has time to register the regions. All
 * subsequent calls are synchronous. In test environments the delay is skipped entirely.
 *
 * Safe to call server-side (no-ops when `document` is unavailable).
 */
export function announce(
  message: string,
  politeness: AnnouncePoliteness = 'polite',
  timeout = 7000
): void {
  if (typeof document === 'undefined') return;

  if (announcer) {
    announcer.announce(message, politeness, timeout);
    return;
  }

  // First call — create the singleton and give AT 100ms to register the live regions.
  announcer = new LiveAnnouncer();

  if (isTestEnvironment()) {
    announcer.announce(message, politeness, timeout);
  } else {
    setTimeout(() => {
      if (announcer?.isAttached()) {
        announcer.announce(message, politeness, timeout);
      }
    }, 100);
  }
}

/**
 * Clears all pending announcements from the specified live region (or both if omitted).
 * Use this before re-announcing the same message to ensure the AT treats it as new content,
 * or on navigation to cancel any in-flight announcements.
 */
export function clearAnnouncer(politeness?: AnnouncePoliteness): void {
  announcer?.clear(politeness);
}

/**
 * Removes the live-announcer container from the DOM and resets the singleton.
 * Primarily useful in tests to reset state between runs.
 */
export function destroyAnnouncer(): void {
  if (announcer) {
    announcer.destroy();
    announcer = null;
  }
}
